import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { INTERNAL_ROUTES } from '@/constants/urls';
import {
  clearPendingSession,
  readPendingSession,
} from '@/components/membership/checkoutPlanStorage';

import type { MembershipSource } from '@/hooks/useOpenMembership';

// Lazy so Stripe.js stays out of the main bundle until someone opens the sheet.
const MembershipDialog = lazy(() => import('@/components/membership/MembershipDialog'));

export interface MembershipOpenRequest {
  source: MembershipSource | null;
  /** Set when Stripe returns a redirect-based payment to `/membership?session_id=`. */
  sessionId?: string | null;
}

interface MembershipDialogContextValue {
  openMembership: (request: MembershipOpenRequest) => void;
}

const MembershipDialogContext = createContext<MembershipDialogContextValue | null>(null);

export const useMembershipDialog = () => useContext(MembershipDialogContext);

/** Owns the support sheet, so it opens over whatever screen the voter is on. */
export function MembershipDialogProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<(MembershipOpenRequest & { key: number }) | null>(null);
  const restoreFocus = useRef<HTMLElement | null>(null);

  const openMembership = useCallback((next: MembershipOpenRequest) => {
    const active = document.activeElement;
    restoreFocus.current = active instanceof HTMLElement ? active : null;
    setRequest({ ...next, key: Date.now() });
  }, []);

  // A reload while a redirect payment was still being confirmed reopens its check. On
  // /membership itself, MembershipLink opens it from the URL instead.
  useEffect(() => {
    const pending = readPendingSession();
    if (pending && !window.location.pathname.startsWith(INTERNAL_ROUTES.MEMBERSHIP)) {
      setRequest({ source: null, sessionId: pending, key: Date.now() });
    }
  }, []);

  const closeMembership = useCallback(() => {
    setRequest(null);
    clearPendingSession();
    const opener = restoreFocus.current;
    // The account menu and Settings close themselves on the way here, so their item is gone;
    // the account button that opened them takes focus instead.
    const target =
      opener?.isConnected && !opener.closest('[aria-hidden="true"]')
        ? opener
        : document.querySelector<HTMLElement>('[data-settings-return-focus="account"]');
    requestAnimationFrame(() => target?.focus());
  }, []);

  const value = useMemo(() => ({ openMembership }), [openMembership]);
  return (
    <MembershipDialogContext.Provider value={value}>
      {children}
      {request && (
        <Suspense fallback={null}>
          <MembershipDialog
            key={request.key}
            source={request.source}
            sessionId={request.sessionId ?? null}
            onClose={closeMembership}
          />
        </Suspense>
      )}
    </MembershipDialogContext.Provider>
  );
}
